import { useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { MessageSquare, Plus, ArrowLeft, Send, Paperclip, FileText, Trash2 } from 'lucide-react';
import { messagesAPI, uploadsAPI } from '../../services/api';
import NewMessageModal from '../../components/messages/NewMessageModal';
import useAuthStore from '../../context/authStore';
import toast from 'react-hot-toast';

import BackButton from '../../components/common/BackButton';
function NewMessageButton({ projectId }) {
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);

  // All client roles can start direct messages; project threads still use project context
  return (
    <>
      <button onClick={() => setOpen(true)} className="btn-primary">
        <Plus size={20} />
        New Message
      </button>
      <NewMessageModal
        isOpen={open}
        onClose={() => setOpen(false)}
        projectId={projectId}
      />
    </>
  );
}

export default function MessagesPage() {
  const { id: projectId } = useParams();
  const [selectedThread, setSelectedThread] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyAttachments, setReplyAttachments] = useState([]);
  const fileInputRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ['messages', projectId],
    queryFn: () => messagesAPI.getAll(projectId ? { projectId } : undefined),
    select: (res) => res.data,
  });

  const threads = data?.threads || [];

  const MAX_UPLOAD_SIZE = 50 * 1024 * 1024; // 50MB
  const ALLOWED_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'image/jpeg',
    'image/png',
    'image/gif',
    'text/plain',
    'text/csv',
  ];

  const uploadWithProgress = (uploadUrl, file, onProgress, onXhr) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    if (onXhr) onXhr(xhr);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        onProgress(percent);
      }
    };
    return new Promise((resolve, reject) => {
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve({ xhr });
        else reject(new Error('Upload failed with status ' + xhr.status));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(file);
    });
  };

  const handleReplyFiles = async (files) => {
    if (!files || !files.length) return;

    for (const file of Array.from(files)) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: File type not allowed`);
        continue;
      }
      if (file.size > MAX_UPLOAD_SIZE) {
        toast.error(`${file.name}: File too large (max 50MB)`);
        continue;
      }

      const id = `${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
      setReplyAttachments((prev) => [
        ...prev,
        { id, file, fileName: file.name, fileSize: file.size, fileType: file.type, progress: 0, status: 'uploading', key: null, fileUrl: null, xhr: null },
      ]);

      try {
        const res = await uploadsAPI.getPresignedUrl({ fileName: file.name, fileType: file.type });
        const { uploadUrl, key, fileUrl } = res.data;
        setReplyAttachments((prev) => prev.map((att) => att.id === id ? { ...att, key, fileUrl } : att));

        await uploadWithProgress(
          uploadUrl,
          file,
          (percent) => {
            setReplyAttachments((prev) => prev.map((att) => att.id === id ? { ...att, progress: percent } : att));
          },
          (xhr) => {
            setReplyAttachments((prev) => prev.map((att) => att.id === id ? { ...att, xhr } : att));
          }
        );

        setReplyAttachments((prev) => prev.map((att) => att.id === id ? { ...att, progress: 100, status: 'uploaded', xhr: null } : att));
      } catch (err) {
        console.error('Upload failed', err);
        setReplyAttachments((prev) => prev.map((att) => att.id === id ? { ...att, status: 'error', xhr: null } : att));
        toast.error('Upload failed for ' + file.name);
      }
    }
  };

  const cancelReplyUpload = (id) => {
    const att = replyAttachments.find((item) => item.id === id);
    if (att && att.xhr) {
      try { att.xhr.abort(); } catch (e) { /* ignore */ }
    }
    setReplyAttachments((prev) => prev.map((item) => item.id === id ? { ...item, status: 'cancelled', progress: 0, xhr: null } : item));
  };

  const retryReplyUpload = async (id) => {
    const att = replyAttachments.find((item) => item.id === id);
    if (!att || !att.file) return;
    setReplyAttachments((prev) => prev.map((item) => item.id === id ? { ...item, status: 'uploading', progress: 0 } : item));
    try {
      const res = await uploadsAPI.getPresignedUrl({ fileName: att.fileName, fileType: att.fileType });
      const { uploadUrl, key, fileUrl } = res.data;
      setReplyAttachments((prev) => prev.map((item) => item.id === id ? { ...item, key, fileUrl } : item));
      await uploadWithProgress(
        uploadUrl,
        att.file,
        (percent) => {
          setReplyAttachments((prev) => prev.map((item) => item.id === id ? { ...item, progress: percent } : item));
        },
        (xhr) => {
          setReplyAttachments((prev) => prev.map((item) => item.id === id ? { ...item, xhr } : item));
        }
      );
      setReplyAttachments((prev) => prev.map((item) => item.id === id ? { ...item, progress: 100, status: 'uploaded', xhr: null } : item));
    } catch (err) {
      console.error('Retry upload failed', err);
      setReplyAttachments((prev) => prev.map((item) => item.id === id ? { ...item, status: 'error', xhr: null } : item));
      toast.error('Retry failed for ' + att.fileName);
    }
  };

  const removeReplyAttachment = (id) => {
    setReplyAttachments((prev) => prev.filter((att) => att.id !== id));
  };

  const replyMutation = useMutation({
    mutationFn: ({ threadId, content, attachments }) => messagesAPI.reply(threadId, { content, attachments }),
    onSuccess: () => {
      setReplyText('');
      setReplyAttachments([]);
      toast.success('Reply sent');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to send reply');
    },
  });

  const handleSendReply = () => {
    if (!selectedThread || !replyText.trim()) return;

    if (replyAttachments.some((att) => att.status === 'uploading')) {
      toast.error('Please wait for uploads to finish.');
      return;
    }

    if (replyAttachments.some((att) => att.status === 'error' || att.status === 'cancelled')) {
      toast.error('Fix failed uploads before sending.');
      return;
    }

    const attachmentsPayload = replyAttachments
      .filter((att) => att.status === 'uploaded')
      .map((att) => ({
        fileName: att.fileName,
        fileUrl: att.fileUrl,
        key: att.key,
        fileSize: att.fileSize,
        fileType: att.fileType,
      }));

    replyMutation.mutate({
      threadId: selectedThread._id,
      content: replyText.trim(),
      attachments: attachmentsPayload,
    });
  };

  return (
    <div className="space-y-6">
      {projectId && (
        <BackButton fallbackTo={`/projects/${projectId}`} plainClassName="inline-flex items-center gap-2 text-secondary-600 hover:text-secondary-900" />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-secondary-900">Messages</h1>
          <p className="mt-1 text-secondary-600">Communication threads with your team</p>
        </div>
        <NewMessageButton projectId={projectId} />
      </div>

      <div className="card">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : threads.length > 0 && projectId ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
            <div className="border border-secondary-200 rounded-lg overflow-hidden bg-secondary-50">
              <div className="bg-secondary-100 px-4 py-3 border-b border-secondary-200">
                <h2 className="text-sm font-semibold text-secondary-900">Conversations</h2>
              </div>
              <div className="divide-y divide-secondary-200">
                {threads.map((thread) => (
                  <button
                    key={thread._id}
                    onClick={() => setSelectedThread(thread)}
                    className={`w-full text-left px-4 py-3 hover:bg-secondary-50 transition-colors ${
                      selectedThread?._id === thread._id ? 'bg-white border-l-4 border-primary-600' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <MessageSquare className="w-4 h-4 text-primary-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-secondary-900 truncate">{thread.subject}</p>
                          {thread.unreadCount > 0 && (
                            <span className="badge badge-primary">{thread.unreadCount}</span>
                          )}
                        </div>
                        <p className="text-xs text-secondary-500 mt-1">
                          {thread.projectId?.name} - {thread.messages?.length || 0} messages
                        </p>
                        <p className="text-xs text-secondary-600 mt-1 truncate">
                          {thread.messages?.[thread.messages.length - 1]?.content || 'No messages'}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="lg:col-span-2 border border-secondary-200 rounded-lg overflow-hidden flex flex-col bg-white">
              {selectedThread ? (
                <>
                  <div className="bg-secondary-50 px-6 py-4 border-b border-secondary-200">
                    <h3 className="font-semibold text-secondary-900">{selectedThread.subject}</h3>
                    <p className="text-xs text-secondary-500 mt-1">
                      {selectedThread.messages?.length || 0} messages
                    </p>
                  </div>
                  <div className="flex-1 overflow-y-auto p-6 space-y-4 max-h-[480px]">
                    {selectedThread.messages?.map((msg, index) => (
                      <div key={index} className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                          {msg.userId?.firstName?.[0] || 'U'}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-secondary-900">
                              {msg.userId?.firstName} {msg.userId?.lastName}
                            </span>
                            <span className="text-xs text-secondary-500">
                              {new Date(msg.createdAt).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-secondary-700 mt-1">{msg.content}</p>
                          {msg.attachments && msg.attachments.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {msg.attachments.map((att) => (
                                <a
                                  key={att.key || att.fileUrl}
                                  href={att.fileUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
                                >
                                  <FileText size={16} />
                                  <span className="truncate">{att.fileName}</span>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-secondary-200 p-4 space-y-2">
                    <div className="flex gap-2">
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Reply..."
                        className="input flex-1"
                        rows={2}
                      />
                      <button
                        onClick={handleSendReply}
                        disabled={replyMutation.isPending || !replyText.trim()}
                        className="btn-primary"
                      >
                        <Send size={18} />
                        Send
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={(e) => handleReplyFiles(e.target.files)}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="px-3 py-1.5 border rounded-lg text-sm hover:bg-secondary-50 flex items-center gap-1"
                      >
                        <Paperclip size={14} />
                        Attach files
                      </button>
                      <div className="text-sm text-secondary-500">Max 50MB per file</div>
                    </div>
                    {replyAttachments.length > 0 && (
                      <div className="space-y-2">
                        {replyAttachments.map((att) => (
                          <div key={att.id} className="flex items-center gap-2 p-2 bg-secondary-50 rounded-lg">
                            <div className="text-secondary-400"><FileText size={16} /></div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm truncate">{att.fileName}</p>
                              {att.status === 'uploading' && (
                                <div className="mt-1 h-2 bg-secondary-200 rounded overflow-hidden">
                                  <div className="h-2 bg-primary-600" style={{ width: `${att.progress}%` }} />
                                </div>
                              )}
                              {att.status === 'error' && (
                                <p className="text-xs text-red-600 mt-1">Upload failed</p>
                              )}
                              {att.status === 'cancelled' && (
                                <p className="text-xs text-secondary-500 mt-1">Upload cancelled</p>
                              )}
                            </div>
                            {att.status === 'uploading' && (
                              <button
                                type="button"
                                onClick={() => cancelReplyUpload(att.id)}
                                className="text-xs text-secondary-600 hover:text-secondary-800"
                              >
                                Cancel
                              </button>
                            )}
                            {(att.status === 'error' || att.status === 'cancelled') && (
                              <button
                                type="button"
                                onClick={() => retryReplyUpload(att.id)}
                                className="text-xs text-secondary-600 hover:text-secondary-800"
                              >
                                Retry
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => removeReplyAttachment(att.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-full text-secondary-500 p-12">
                  <MessageSquare className="w-10 h-10 text-secondary-300 mr-3" />
                  <span>Select a conversation to view messages</span>
                </div>
              )}
            </div>
          </div>
        ) : threads.length > 0 ? (
          <div className="divide-y divide-secondary-200">
            {threads.map((thread) => (
              <Link
                key={thread._id}
                to={`/messages/${thread._id}`}
                className="flex items-start gap-4 p-4 hover:bg-secondary-50 transition-colors"
              >
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-primary-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-secondary-900 truncate">{thread.subject}</p>
                    {thread.unreadCount > 0 && (
                      <span className="badge badge-primary">{thread.unreadCount} new</span>
                    )}
                  </div>
                  <p className="text-sm text-secondary-500 mt-1">
                    {thread.projectId?.name} - {thread.messages?.length || 0} messages
                  </p>
                  <p className="text-sm text-secondary-600 mt-1 truncate">
                    {thread.messages?.[thread.messages.length - 1]?.content || 'No messages'}
                  </p>
                </div>
                <span className="text-xs text-secondary-400 flex-shrink-0">
                  {new Date(thread.lastActivityAt).toLocaleDateString()}
                </span>
              </Link>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-secondary-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-secondary-300" />
            <p>No message threads yet</p>
            <NewMessageButton projectId={projectId} />
          </div>
        )}
      </div>
    </div>
  );
}
